import { useEffect, useRef, useState } from 'react'
import { create } from 'zustand'
import { Button, Dialog, Input } from '@/components/ui'

/**
 * In-app confirm and text prompts. The browser's own confirm()/prompt() are
 * never used: they look foreign and block the page.
 */
type Ask = {
  title: string
  body?: string
  confirm?: string
  danger?: boolean
  /** Set to ask for text; the promise resolves with it (or null). */
  input?: string
  resolve: (v: boolean | string | null) => void
}

const useAsk = create<{ current: Ask | null; set: (a: Ask | null) => void }>((set) => ({ current: null, set: (current) => set({ current }) }))

export function ask(o: Omit<Ask, 'resolve' | 'input'>): Promise<boolean> {
  return new Promise((resolve) => useAsk.getState().set({ ...o, resolve: (v) => resolve(v === true) }))
}

export function askText(o: Omit<Ask, 'resolve' | 'input'> & { value?: string }): Promise<string | null> {
  return new Promise((resolve) => useAsk.getState().set({ ...o, input: o.value ?? '', resolve: (v) => resolve(typeof v === 'string' ? v : null) }))
}

export function AskHost() {
  const { current, set } = useAsk()
  const [text, setText] = useState('')
  const ok = useRef<HTMLButtonElement>(null)
  useEffect(() => { if (current) setText(current.input ?? '') }, [current])
  if (!current) return null
  const done = (v: boolean | string | null) => { current.resolve(v); set(null) }
  const submit = () => done(current.input !== undefined ? text.trim() || null : true)
  return (
    <Dialog open onOpenChange={(o) => !o && done(current.input !== undefined ? null : false)} title={current.title} width={420}>
      <form onSubmit={(e) => { e.preventDefault(); submit() }} className="space-y-4">
        {current.body && <p className="text-sm text-fg-muted">{current.body}</p>}
        {current.input !== undefined && <Input autoFocus value={text} onChange={(e) => setText(e.target.value)} />}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={() => done(current.input !== undefined ? null : false)}>Cancel</Button>
          <Button ref={ok} autoFocus={current.input === undefined} type="submit" variant={current.danger ? 'danger' : 'primary'}>{current.confirm ?? 'OK'}</Button>
        </div>
      </form>
    </Dialog>
  )
}
